import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';

import {
  getActionOptions,
  getConditionOptions,
} from 'dashboard/helper/automationHelper';
import {
  PRIORITY_CONDITION_VALUES,
  CALL_DIRECTION_VALUES,
  MISSED_CALL_VALUES,
  EMAIL_AUTO_REPLY_VALUES,
  YES_NO_VALUES,
  MESSAGE_SOURCE_TYPE_VALUES,
  CHANNEL_TYPE_VALUES,
} from 'dashboard/constants/automation';

/**
 * This is a shared composables that holds utilites used to build dropdown and file options
 * @returns {Object} An object containing various automation-related functions and computed properties.
 */
export default function useAutomationValues() {
  const getters = useStoreGetters();
  const { t } = useI18n();
  const agents = useMapGetter('agents/getAgents');
  const campaigns = useMapGetter('campaigns/getAllCampaigns');
  const contacts = useMapGetter('contacts/getContacts');
  const inboxes = useMapGetter('inboxes/getInboxesWithInternal');
  // actions start new work in the inbox, so they skip the inboxes marked for deletion;
  // conditions still match the conversations of those inboxes
  const activeInboxes = useMapGetter('inboxes/getActiveInboxesWithInternal');
  const labels = useMapGetter('labels/getLabels');
  const teams = useMapGetter('teams/getTeams');
  const slaPolicies = useMapGetter('sla/getSLA');

  const booleanFilterOptions = computed(() => [
    { id: true, name: t('FILTER.ATTRIBUTE_LABELS.TRUE') },
    { id: false, name: t('FILTER.ATTRIBUTE_LABELS.FALSE') },
  ]);

  const messageTypeOptions = computed(() => [
    { id: 'incoming', name: t('AUTOMATION.MESSAGE_TYPE_VALUES.INCOMING') },
    { id: 'outgoing', name: t('AUTOMATION.MESSAGE_TYPE_VALUES.OUTGOING') },
    { id: 'private', name: t('AUTOMATION.MESSAGE_TYPE_VALUES.PRIVATE') },
    { id: 'ai_summary', name: t('AUTOMATION.MESSAGE_TYPE_VALUES.AI_SUMMARY') },
  ]);

  const priorityOptions = computed(() =>
    PRIORITY_CONDITION_VALUES.map(({ id, i18nKey }) => ({
      id,
      name: t(`CONVERSATION.PRIORITY.OPTIONS.${i18nKey}`),
    }))
  );

  // Options with an i18nKey get the translated label; brand names keep `name`.
  const translateOptions = options =>
    options.map(({ id, name, i18nKey }) => ({
      id,
      name: i18nKey ? t(i18nKey) : name,
    }));

  const callDirectionOptions = computed(() =>
    translateOptions(CALL_DIRECTION_VALUES)
  );
  const missedCallOptions = computed(() =>
    translateOptions(MISSED_CALL_VALUES)
  );
  const emailAutoReplyOptions = computed(() =>
    translateOptions(EMAIL_AUTO_REPLY_VALUES)
  );
  const yesNoOptions = computed(() => translateOptions(YES_NO_VALUES));
  const messageSourceTypeOptions = computed(() =>
    translateOptions(MESSAGE_SOURCE_TYPE_VALUES)
  );
  const channelTypeOptions = computed(() =>
    translateOptions(CHANNEL_TYPE_VALUES)
  );

  const statusFilterItems = computed(() => {
    return {
      open: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open.TEXT'),
      },
      resolved: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT'),
      },
      pending: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.pending.TEXT'),
      },
      snoozed: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT'),
      },
      all: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.all.TEXT'),
      },
    };
  });

  const statusFilterOptions = computed(() => {
    const statusFilters = statusFilterItems.value;
    return Object.keys(statusFilters).map(status => ({
      id: status,
      name: statusFilters[status].TEXT,
    }));
  });

  /**
   * Adds a translated "None" option to the beginning of a list
   * @param {Array} list - The list to add "None" to
   * @returns {Array} A new array with "None" option at the beginning
   */
  const addNoneToList = list => [
    {
      id: 'nil',
      name: t('AUTOMATION.NONE_OPTION') || 'None',
    },
    ...(list || []),
  ];

  /**
   * Gets the condition dropdown values for a given type.
   * @param {string} type - The type of condition.
   * @returns {Array} An array of condition dropdown values.
   */
  const getConditionDropdownValues = type => {
    return getConditionOptions({
      agents: agents.value,
      booleanFilterOptions: booleanFilterOptions.value,
      campaigns: campaigns.value,
      contacts: contacts.value,
      customAttributes: getters['attributes/getAttributes'].value,
      inboxes: inboxes.value,
      labels: labels.value,
      statusFilterOptions: statusFilterOptions.value,
      teams: teams.value,
      messageTypeOptions: messageTypeOptions.value,
      priorityOptions: priorityOptions.value,
      callDirectionOptions: callDirectionOptions.value,
      missedCallOptions: missedCallOptions.value,
      emailAutoReplyOptions: emailAutoReplyOptions.value,
      yesNoOptions: yesNoOptions.value,
      messageSourceTypeOptions: messageSourceTypeOptions.value,
      channelTypeOptions: channelTypeOptions.value,
      languages,
      countries,
      type,
    });
  };

  /**
   * Gets the action dropdown values for a given type.
   * @param {string} type - The type of action.
   * @returns {Array} An array of action dropdown values.
   */
  const getActionDropdownValues = type => {
    return getActionOptions({
      agents: agents.value,
      labels: labels.value,
      teams: teams.value,
      slaPolicies: slaPolicies.value,
      inboxes: activeInboxes.value,
      languages,
      type,
      addNoneToListFn: addNoneToList,
      priorityOptions: priorityOptions.value,
    });
  };

  return {
    booleanFilterOptions,
    statusFilterItems,
    statusFilterOptions,
    getConditionDropdownValues,
    getActionDropdownValues,
    agents,
    campaigns,
    contacts,
    inboxes,
    labels,
    teams,
    slaPolicies,
  };
}
